import { collection, doc, getDoc, query, where } from "firebase/firestore";
import { computed, ref, watch, type Ref } from "vue";
import { useCollection, useCurrentUser, useDocument } from "vuefire";
import { db } from "./firebase";
import type { Experiment, Folder, Method, Workspace } from "./model";
import { useRouter } from "vue-router";
import { useSuperAdminClaims } from "@/admin/useSuperAdminClaims";
import { currentTeamId } from "@/state/teamCapabilities";

/** Returns `name` if set, otherwise capitalizes the workspace ID. */
export function workspaceDisplayName(id: string, name?: string): string {
  return name || id[0].toUpperCase() + id.slice(1);
}

/** The subset of a workspace doc the overview needs: display name + owner UID. */
export interface WorkspaceMeta {
  name: string | undefined;
  owner: string | undefined; // owner UID
}

/**
 * Fetches workspace docs for a list of IDs and returns a reactive map of
 * ID → { name, owner } (each field undefined if the doc lacks it).
 *
 * Callers commonly pass a computed that builds a fresh array on every render
 * (e.g. derived from a live Firestore snapshot). Watching the array reference
 * would refetch every workspace doc on each render even when the actual set of
 * IDs is unchanged. Key the watch on the sorted, comma-joined IDs so we only
 * refetch when the membership genuinely changes.
 */
export function useWorkspaceMetas(workspaceIds: Ref<string[]>) {
  const metas = ref<Record<string, WorkspaceMeta>>({});

  const idsKey = computed(() => [...workspaceIds.value].sort().join(","));

  watch(
    idsKey,
    async () => {
      const ids = workspaceIds.value;
      const result: Record<string, WorkspaceMeta> = {};
      await Promise.all(
        ids.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, `workspaces/${id}`));
            const data = snap.data();
            result[id] = { name: data?.name, owner: data?.owner };
          } catch {
            // Workspace doc may not be accessible
          }
        })
      );
      metas.value = result;
    },
    { immediate: true }
  );

  return metas;
}

/**
 * Reactive map of workspace ID → name (undefined if unset). A thin wrapper over
 * `useWorkspaceMetas` so existing callers keep their string-map contract.
 */
export function useWorkspaceNames(workspaceIds: Ref<string[]>) {
  const metas = useWorkspaceMetas(workspaceIds);
  return computed<Record<string, string | undefined>>(() => {
    const names: Record<string, string | undefined> = {};
    for (const [id, meta] of Object.entries(metas.value)) {
      names[id] = meta.name;
    }
    return names;
  });
}

// Shared workspace member
export interface WorkspaceMember {
  uid?: string; // member doc ID (Firebase UID) — used to resolve email via Auth
  emailId: string;
  role: "Owner" | "Editor" | "Viewer";
  invited?: boolean;
}

/**
 * A team invite doc (`teams/{teamId}/invites/{inviteId}`). The `workspace*`
 * fields are present only on "combined" invites created by `addWorkspaceMember`
 * — those grant a workspace seat on accept, and are what we surface as pending
 * members in the workspace Manage panel.
 */
interface TeamInvite {
  emailId: string;
  workspaceId?: string;
  workspaceRole?: WorkspaceMember["role"];
}

const BRIEFLY_DOMAIN = "@briefly.bio";

/**
 * Picks the email to display as a workspace's "owner", from its members.
 * - Personal workspace (ownerUid set): the member whose doc id === ownerUid.
 * - Shared workspace (ownerUid empty): among Owner-role members with an email,
 *   prefer non-@briefly.bio addresses (alphabetical), else any owner
 *   (alphabetical). Returns undefined when none qualifies.
 */
export function selectWorkspaceOwnerEmail(
  members: WorkspaceMember[],
  ownerUid: string | undefined
): string | undefined {
  if (ownerUid) {
    return members.find((m) => m.uid === ownerUid)?.emailId || undefined;
  }
  const owners = members
    .filter((m) => m.role === "Owner" && m.emailId)
    .map((m) => m.emailId)
    .sort((a, b) => a.localeCompare(b));
  if (owners.length === 0) return undefined;
  const external = owners.filter(
    (e) => !e.toLowerCase().endsWith(BRIEFLY_DOMAIN)
  );
  return external[0] ?? owners[0];
}

export type WorkspaceRole =
  | "Personal"
  | "Owner"
  | "Editor"
  | "Viewer"
  | "SuperAdminViewer"
  | "None";

// If called without the workspaceId - will get it from the router
export const useWorkspace = (forWorkspaceId?: string) => {
  // DONT USE useRouteParams() - it doesn't work as expected
  const route = useRouter().currentRoute;
  const workspaceId = computed(
    () => forWorkspaceId ?? (route.value.params["workspaceId"] as string)
  );

  const workspaceRef = computed(() =>
    workspaceId.value ? doc(db, `workspaces/${workspaceId.value}`) : null
  );
  const workspace = useDocument<Workspace>(workspaceRef);
  const user = useCurrentUser();
  const { isSuperAdmin, ready: superAdminReady } = useSuperAdminClaims();
  const sharedWorkspaceRole = useDocument<WorkspaceMember>(
    computed(() =>
      workspaceId.value && user.value?.uid
        ? doc(db, `workspaces/${workspaceId.value}/members/${user.value.uid}`)
        : undefined
    )
  );
  const workspaceRole = computed<WorkspaceRole | undefined>(() => {
    // Noone logged in
    if (user.value === null) {
      return "None";
    }
    if (!workspace.value || user.value === undefined) {
      // Not yet known.
      return undefined;
    }
    // Determine membership first; the super-admin claim is only consulted
    // when the user is definitively NOT a member. We wait for the member
    // doc to load before falling through, so a super admin who is also a
    // member never briefly renders as "SuperAdminViewer".
    if (workspace.value.owner) {
      if (workspace.value.owner === user.value.uid) {
        return "Personal";
      }
      // Not the owner — fall through to super-admin check below.
    } else {
      if (sharedWorkspaceRole.pending.value) {
        return undefined;
      }
      if (sharedWorkspaceRole.value?.role) {
        return sharedWorkspaceRole.value.role;
      }
      // No membership doc — fall through to super-admin check below.
    }
    // Not a member of this workspace. Wait for the claim to load before
    // reporting "None", to avoid a flash of PrivateMethodMessage for a
    // true super admin.
    if (!superAdminReady.value) {
      return undefined;
    }
    return isSuperAdmin.value ? "SuperAdminViewer" : "None";
  });
  const isOwner = computed(() => workspaceRole.value === "Owner");
  const members = useCollection<WorkspaceMember>(
    computed(() =>
      workspaceId.value && isOwner.value
        ? collection(db, `workspaces/${workspaceId.value}/members`)
        : undefined
    )
  );
  const invites = useCollection<WorkspaceMember>(
    computed(() =>
      workspaceId.value && isOwner.value
        ? collection(db, `workspaces/${workspaceId.value}/invites`)
        : undefined
    )
  );

  // "Combined" team+workspace invites (created by `addWorkspaceMember` when the
  // inviter is on a team and the invitee is not) live under the inviter's team
  // at `teams/{teamId}/invites`, NOT under this workspace — so the `invites`
  // listener above never sees them. Read the current user's own team invites
  // filtered to this workspace; they're the ones that grant a seat here.
  const teamInvites = useCollection<TeamInvite>(
    computed(() =>
      workspaceId.value && isOwner.value && currentTeamId.value
        ? query(
            collection(db, `teams/${currentTeamId.value}/invites`),
            where("workspaceId", "==", workspaceId.value)
          )
        : undefined
    )
  );
  // Normalize combined invites to the WorkspaceMember shape the members list
  // expects (their workspace role is stored under `workspaceRole`).
  const combinedInvites = computed<WorkspaceMember[]>(() =>
    teamInvites.value
      .filter((i) => !!i.workspaceRole)
      .map((i) => ({
        emailId: i.emailId,
        role: i.workspaceRole as WorkspaceMember["role"],
        invited: true,
      }))
  );

  const allMethods = useCollection<Method>(
    computed(() =>
      workspaceId.value
        ? collection(db, `workspaces/${workspaceId.value}/methods`)
        : null
    )
  );
  const allExperiments = useCollection<Experiment>(
    computed(() =>
      workspaceId.value
        ? collection(db, `workspaces/${workspaceId.value}/experiments`)
        : null
    )
  );
  const allFolders = useCollection<Folder>(
    computed(() =>
      workspaceId.value
        ? collection(db, `workspaces/${workspaceId.value}/folders`)
        : null
    )
  );
  const methods = computed(() => allMethods.value.filter((m) => !m.archived));
  const experiments = computed(() =>
    allExperiments.value.filter((m) => !m.archived)
  );
  const folders = computed(() => allFolders.value.filter((f) => !f.archived));
  const trash = computed(() => {
    // With folders trash isn't as simple as just all `archived` files.
    // We want to be fail-safe against "move to trash" on the folder operation
    // failing in the middle and some documents staying `archived=false` but being
    // in the `archived` folder. We will display such documents in the trash so
    // that the user can restore them. So we will be careful to return only
    // `archived` docs at the top level but all the docs with parentFolderId and
    // let `buildData` figure out if those documents are orphaned and need to be
    // displayed in trash.
    const maybeTrash = (d: Folder | Method | Experiment) =>
      d.archived || d.parentFolderId;
    return {
      methods: allMethods.value.filter(maybeTrash),
      experiments: allExperiments.value.filter(maybeTrash),
      folders: allFolders.value.filter(maybeTrash),
    };
  });

  const canEdit = computed(() => {
    // Yet unknown.
    if (workspaceRole.value == undefined) {
      return undefined;
    }

    return ["Personal", "Owner", "Editor"].includes(workspaceRole.value);
  });

  return {
    workspaceId,
    workspace,
    workspaceRef,
    workspaceRole,
    members,
    invites,
    combinedInvites,
    methods,
    experiments,
    trash,
    folders,
    canEdit,
  };
};
